import { Job, JobStatus, StatusChange, STATUS_TRANSITIONS } from '../pages/job-summary/Interfaces';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

// Priority levels for conflict resolution
const STATUS_PRIORITY: Record<JobStatus, number> = {
  'Archived': 1,                  // Highest - nothing should auto-change archived jobs
  'Cancelled': 2,                 // High - cancelled work stays cancelled
  'Closed': 3,                    // High - completed work shouldn't regress automatically
  'Active': 4,                    // Medium-high - respect active work sessions
  'Awaiting Client Response': 5,  // Medium - blocks other transitions but can be overridden
  'Overdue': 6,                   // Medium-low - important but flexible
  'Open': 7,                      // Low - very flexible
  'Pending': 8                    // Lowest - most flexible
};

export class JobStatusService {
  /**
   * Check if a status transition is valid.
   * Defensive against corrupt / legacy job docs: if `from` isn't one of
   * the canonical JobStatus values, we treat the transition as allowed.
   * That lets a user repair a job that somehow got into a bad state
   * (e.g. a legacy 'Complete' value from an old import) by moving it to
   * any valid status.
   */
  static isValidTransition(from: JobStatus, to: JobStatus): boolean {
    const allowed = STATUS_TRANSITIONS[from];
    if (!allowed) {
      // eslint-disable-next-line no-console
      console.warn(`[JobStatusService] unknown current status "${from}" — treating as transition-allowed for recovery`);
      return true;
    }
    return allowed.includes(to);
  }

  /**
   * Check if a job can have automated transitions
   */
  static canAutoTransition(job: Job): boolean {
    // Safely handle jobs without automation field
    const automation = job.automation;
    if (!automation) return true; // Default to enabled for backward compatibility
    
    return automation.enabled && !automation.blockAutoTransitions;
  }

  /**
   * Resolve conflicts between manual and automatic status changes
   * Returns the winning status change or null if the change should be rejected
   */
  static resolveConflict(
    job: Job,
    proposedChange: {
      toStatus: JobStatus;
      changedBy: 'user' | 'system' | 'email-trigger' | 'activity-timeout';
      reason?: string;
    }
  ): { allowed: boolean; reason?: string } {
    const currentStatus = job.status;
    const { toStatus, changedBy } = proposedChange;

    // Manual user changes always take precedence
    if (changedBy === 'user') {
      return { allowed: true };
    }

    // Check if transition is valid
    if (!this.isValidTransition(currentStatus, toStatus)) {
      return { 
        allowed: false, 
        reason: `Invalid transition from ${currentStatus} to ${toStatus}` 
      };
    }

    // "Awaiting Client Response" blocks all auto-transitions except email triggers
    if (currentStatus === 'Awaiting Client Response' && changedBy !== 'email-trigger') {
      return { 
        allowed: false, 
        reason: 'Job is awaiting client response - only email triggers can change status' 
      };
    }

    // Jobs with blocked automation cannot auto-transition
    if (!this.canAutoTransition(job)) {
      return { 
        allowed: false, 
        reason: 'Job automation is disabled or blocked' 
      };
    }

    // Check priority - higher priority statuses resist changes
    const currentPriority = STATUS_PRIORITY[currentStatus];
    const proposedPriority = STATUS_PRIORITY[toStatus];
    
    if (currentPriority < proposedPriority) {
      return { 
        allowed: false, 
        reason: `Current status ${currentStatus} has higher priority than ${toStatus}` 
      };
    }

    // Check if there's been a recent manual change (within last 5 minutes)
    const lastChange = job.automation?.lastStatusChange;
    if (lastChange && lastChange.changedBy === 'user') {
      const lastChangeTime = new Date(lastChange.timestamp);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      
      if (lastChangeTime > fiveMinutesAgo) {
        return { 
          allowed: false, 
          reason: 'Recent manual change detected - automation paused' 
        };
      }
    }

    return { allowed: true };
  }

  /**
   * Update job status with proper validation and conflict resolution
   */
  static async updateJobStatus(
    jobId: string,
    toStatus: JobStatus,
    changedBy: 'user' | 'system' | 'email-trigger' | 'activity-timeout',
    reason?: string,
    userId?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const jobRef = doc(db, 'jobs', jobId);
      const jobDoc = await getDoc(jobRef);

      if (!jobDoc.exists()) {
        return { success: false, error: 'Job not found' };
      }

      const currentJobData = jobDoc.data() as Job;
      if ((currentJobData as any).support) {
        return { success: false, error: 'Monthly support job status follows its required duties. Update a duty from Monthly support or the job workspace.' };
      }
      const currentStatus = currentJobData.status;

      // Allow same-status writes (refresh). User-initiated changes are
      // always allowed — the UI is the trusted escape hatch for legacy
      // or corrupted status values. Automated changes still have to pass
      // the transition rules.
      if (currentStatus !== toStatus
          && changedBy !== 'user'
          && !this.isValidTransition(currentStatus, toStatus)) {
        return {
          success: false,
          error: `Invalid transition from ${currentStatus} to ${toStatus}`,
        };
      }

      const statusChange: StatusChange = {
        timestamp: new Date().toISOString(),
        fromStatus: currentStatus,
        toStatus,
        changedBy,
        reason,
        userId: userId || 'system',
      };

      const updateData: any = {
        status: toStatus,
        lastEdited: new Date().toISOString(),
        'automation.lastStatusChange': statusChange,
        statusHistory: [...(currentJobData.statusHistory || []), statusChange],
      };

      await updateDoc(jobRef, updateData);
      return { success: true };
    } catch (error) {
      console.error('[updateJobStatus] Error updating job status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }


  /**
   * Handle email-triggered status change
   */
  static async handleEmailTrigger(
    customerId: string,
    emailId: string
  ): Promise<{ success: boolean; updatedJobs: string[] }> {
    try {
      // Query for jobs with this customerId that are "Awaiting Client Response"
      // This would be a Firestore query in the real implementation
      
      const updatedJobs: string[] = [];
      
      // For each matching job, update status to "Open"
      // await this.updateJobStatus(jobId, 'Open', 'email-trigger', `Email received: ${emailId}`);
      
      return { success: true, updatedJobs };
    } catch (error) {
      console.error('Error handling email trigger:', error);
      return { success: false, updatedJobs: [] };
    }
  }

  /**
   * Get status badge color for UI
   */
  static getStatusColor(status: JobStatus): string {
    // Aligned with STATUS_CONFIG in dashboard/utils/statusHelpers.ts
    const colors: Record<JobStatus, string> = {
      'Active': '#dc3545',                    // Red (danger) - actively being worked on
      'Open': '#0095E8',                      // Blue (primary)
      'Overdue': '#fd7e14',                   // Orange
      'Pending': '#ffc107',                   // Yellow (warning)
      'Awaiting Client Response': '#ffc107',  // Yellow (warning)
      'Closed': '#198754',                    // Green (success)
      'Cancelled': '#6c757d',                 // Gray (secondary)
      'Archived': '#6c757d'                   // Gray (secondary)
    };
    
    return colors[status];
  }

  /**
   * Get human-readable status display text
   */
  static getStatusDisplayText(status: JobStatus): string {
    const displayText: Record<JobStatus, string> = {
      'Pending': 'Pending Start',
      'Open': 'Ready to Start',
      'Active': 'In Progress',
      'Awaiting Client Response': 'Awaiting Response',
      'Overdue': 'Overdue',
      'Closed': 'Completed',
      'Cancelled': 'Cancelled',
      'Archived': 'Archived'
    };
    
    return displayText[status];
  }
}

export default JobStatusService;
